/**
 * flow-discovery.ts — multi-root flow discovery for the CLI.
 *
 * Walks every {@link aiResourceRoots} root (bundled factory-assets when
 * installed, the monorepo `ai-assets/` in dev, plus `~/.skaile/libraries/`)
 * through the layout-tolerant `loadAllFlows`, so built-in flows are discovered
 * after `npm i` exactly as they are in the monorepo. De-duplicated by flow id
 * (first root wins).
 */

import fs from "node:fs";
import path from "node:path";
import { assetSearchDirs } from "@skaile/workspaces/core";
import { loadAllFlows } from "@skaile/workspaces/factory-assets/connectors/flow/engine";
import { aiResourceRoots } from "./paths.ts";

type FlowDefinition = ReturnType<typeof loadAllFlows>[number];

/** All flows across every content root, de-duplicated by id. */
export function discoverFlows(): FlowDefinition[] {
  const byId = new Map<string, FlowDefinition>();
  for (const root of aiResourceRoots()) {
    for (const flow of loadAllFlows(root)) {
      if (!byId.has(flow.id)) byId.set(flow.id, flow);
    }
  }
  return [...byId.values()];
}

/**
 * Locate the on-disk flow file for `flowId` across every content root, trying
 * both the flat (`<root>/flows/`) and domain-nested (`<root>/<domain>/flows/`)
 * layouts and the `.flow.yaml` / `.flow.json` / legacy `.json` extensions.
 *
 * `priorityRoots` are searched before the standard {@link aiResourceRoots} so a
 * project-local `ai-assets/` tree can override a built-in flow.
 */
export function findFlowFile(flowId: string, priorityRoots: string[] = []): string | undefined {
  for (const root of [...priorityRoots, ...aiResourceRoots()]) {
    for (const { dir } of assetSearchDirs(root, "flows")) {
      for (const ext of [".flow.yaml", ".flow.json", ".json"]) {
        const candidate = path.join(dir, `${flowId}${ext}`);
        if (fs.existsSync(candidate)) return candidate;
      }
    }
  }
  return undefined;
}
